import { type ReactNode, useId, useLayoutEffect, useState } from 'react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, ButtonBase, Container, Stack, Typography } from '@mui/material';

import { cardSx, chipSx, type ChipTone, wf } from '../shell/tokens';

const intakeJourneyStages = ['agreements', 'intake', 'verification', 'go-live'] as const;
export type IntakeJourneyStage = (typeof intakeJourneyStages)[number];
export type IntakeJourneyContent = ReactNode | ((isActive: boolean) => ReactNode);

const labels: Record<IntakeJourneyStage, string> = {
  agreements: 'Agreements & setup',
  intake: 'Intake form',
  verification: 'JotPsych verification',
  'go-live': 'Go Live',
};

export interface IntakeJourneyStageState {
  statusLabel: string;
  /** One-line hint under the status, e.g. what the step covers. */
  caption?: string;
  /** Show the step as done. Only the owner of the stage's completion signal may set this. */
  completed?: boolean;
  /** Chip color. Defaults: completed green, locked grey, selected blue, otherwise grey. */
  tone?: ChipTone;
  lockedReason?: string;
}

/** Presentation only. The caller supplies authoritative status and navigation decisions. */
export interface IntakeJourneyShellProps {
  /** Remove journey chrome without remounting existing intake during a gate fallback. */
  showJourneyChrome?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  stages: Record<IntakeJourneyStage, IntakeJourneyStageState>;
  /** Activity callbacks must return a stable tree; pass the flag through instead of unmounting. */
  content: Record<IntakeJourneyStage, IntakeJourneyContent>;
  toolbar?: ReactNode;
  kickoff?: ReactNode;
}

/** A lock pauses previously available content without discarding its local draft. */
function JourneyStageContent({
  content,
  enabled,
  active,
  lockedReason,
}: {
  content: IntakeJourneyContent;
  enabled: boolean;
  active: boolean;
  lockedReason?: string;
}): React.JSX.Element {
  const [hasBeenAvailable, setHasBeenAvailable] = useState(enabled && !lockedReason);
  useLayoutEffect(() => {
    if (!enabled) setHasBeenAvailable(false);
    else if (!lockedReason) setHasBeenAvailable(true);
  }, [enabled, lockedReason]);
  return (
    <>
      {enabled && lockedReason && (
        <Box sx={cardSx}>
          <Typography>{lockedReason}</Typography>
        </Box>
      )}
      <Box
        hidden={Boolean(lockedReason)}
        inert={!active}
        aria-hidden={!active || undefined}
        sx={lockedReason ? undefined : { display: 'contents' }}
      >
        {enabled && (!lockedReason || hasBeenAvailable)
          ? typeof content === 'function'
            ? content(active)
            : content
          : null}
      </Box>
    </>
  );
}

function StepMarker({
  index,
  completed,
  locked,
  selected,
}: {
  index: number;
  completed: boolean;
  locked: boolean;
  selected: boolean;
}) {
  const tone = completed
    ? { bgcolor: wf.green, color: wf.surface, borderColor: wf.green }
    : selected
      ? { bgcolor: wf.blue, color: wf.surface, borderColor: wf.blue }
      : locked
        ? { bgcolor: wf.surfaceAlt, color: wf.faint, borderColor: wf.line }
        : { bgcolor: wf.surface, color: wf.muted, borderColor: wf.line };
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: '1px solid',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 13,
        fontWeight: 700,
        ...tone,
      }}
    >
      {completed ? (
        <CheckOutlinedIcon sx={{ fontSize: 16 }} />
      ) : locked ? (
        <LockOutlinedIcon sx={{ fontSize: 14 }} />
      ) : (
        index + 1
      )}
    </Box>
  );
}

/** Keep supplied content mounted across stage changes; the owner retains scope/save guards. */
export function IntakeJourneyShell({
  showJourneyChrome = true,
  selectedStage,
  onNavigate,
  stages,
  content,
  toolbar,
  kickoff,
}: IntakeJourneyShellProps): React.JSX.Element {
  const id = useId();
  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={showJourneyChrome ? undefined : { display: 'contents' }}
    >
      <Stack
        spacing={showJourneyChrome ? 3 : 0}
        sx={showJourneyChrome ? { p: { xs: 2, md: 4 }, color: wf.ink } : { display: 'contents' }}
      >
        {showJourneyChrome && (
          <Stack spacing={1}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 0.5, md: 2 }}
              sx={{ alignItems: { md: 'baseline' } }}
            >
              <Typography variant="h4" component="h1">
                Get billing-ready
              </Typography>
              <Typography color="text.secondary">Track your practice’s setup progress.</Typography>
            </Stack>
            {toolbar}
          </Stack>
        )}
        {showJourneyChrome && (
          <Box
            component="nav"
            aria-label="Practice setup stages"
            sx={{
              bgcolor: wf.surface,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: { xs: 1, md: 0 },
              borderBottom: { md: `1px solid ${wf.line}` },
            }}
          >
            {intakeJourneyStages.map((stage, index) => {
              const selected = stage === selectedStage;
              const { statusLabel, caption, completed = false, tone, lockedReason } = stages[stage];
              const locked = Boolean(lockedReason);
              const chipTone: ChipTone = completed
                ? 'ok'
                : locked
                  ? 'mut'
                  : (tone ?? (selected ? 'info' : 'mut'));
              const last = index === intakeJourneyStages.length - 1;
              return (
                <ButtonBase
                  key={stage}
                  id={`${id}-${stage}-button`}
                  aria-current={selected ? 'step' : undefined}
                  aria-controls={`${id}-${stage}-panel`}
                  aria-describedby={`${id}-${stage}-status`}
                  aria-disabled={locked}
                  onClick={() => {
                    if (!locked) onNavigate(stage);
                  }}
                  sx={{
                    ...cardSx,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    textAlign: 'left',
                    gap: 0.5,
                    border: { xs: `1px solid ${selected ? wf.blue : wf.line}`, md: 0 },
                    borderRadius: { xs: '8px', md: 0 },
                    borderBottom: { md: `3px solid ${selected ? wf.blue : 'transparent'}` },
                    bgcolor: { xs: selected ? wf.blueSoft : wf.surface, md: 'transparent' },
                    color: locked ? wf.muted : wf.ink,
                    '&.Mui-focusVisible': { outline: `3px solid ${wf.blue}`, outlineOffset: 2 },
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <StepMarker
                      index={index}
                      completed={completed}
                      locked={locked}
                      selected={selected}
                    />
                    <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3, flex: 1 }}>
                      {labels[stage]}
                    </Typography>
                    {!last && (
                      <ChevronRightOutlinedIcon
                        aria-hidden="true"
                        sx={{ fontSize: 18, color: wf.faint, display: { xs: 'none', md: 'block' } }}
                      />
                    )}
                  </Stack>
                  <Box sx={{ pl: '38px' }}>
                    <Box component="span" sx={chipSx(chipTone)}>
                      {locked ? 'Locked' : statusLabel}
                    </Box>
                    <Typography
                      id={`${id}-${stage}-status`}
                      variant="caption"
                      component="p"
                      sx={{ color: wf.muted, mt: 1, lineHeight: 1.4 }}
                    >
                      {lockedReason ?? caption ?? statusLabel}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        )}
        {intakeJourneyStages.map(stage => (
          <Box
            key={stage}
            component="section"
            id={`${id}-${stage}-panel`}
            aria-labelledby={showJourneyChrome ? `${id}-${stage}-button` : undefined}
            hidden={
              showJourneyChrome
                ? stage !== selectedStage
                : stage !== 'intake' && stage !== 'go-live'
            }
            sx={{
              minWidth: 0,
              ...(!showJourneyChrome && (stage === 'intake' || stage === 'go-live')
                ? { display: 'contents' }
                : {}),
            }}
          >
            <JourneyStageContent
              content={content[stage]}
              enabled={showJourneyChrome || stage === 'intake' || stage === 'go-live'}
              lockedReason={showJourneyChrome ? stages[stage].lockedReason : undefined}
              active={
                showJourneyChrome
                  ? stage === selectedStage && !stages[stage].lockedReason
                  : stage === 'intake' || stage === 'go-live'
              }
            />
          </Box>
        ))}
        {showJourneyChrome && kickoff}
      </Stack>
    </Container>
  );
}
